import { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { getHorarioLaboral, putHorarioLaboral } from '../../api/client'
import { usePuedeEditarMantenimiento } from '../../utils/mantenimiento'

const DIAS = ['lunes', 'martes', 'miercoles', 'jueves', 'viernes', 'sabado', 'domingo']
const hhmm = (v) => v?.slice(0, 5) || ''
const aForm = (x) => ({ ...x, inicio: hhmm(x.inicio), fin: hhmm(x.fin), colacion_inicio: hhmm(x.colacion_inicio), colacion_fin: hhmm(x.colacion_fin) })
// A day's lunch, if any, must lie strictly inside its jornada.
const colacionMala = (d) => (d.colacion_inicio || d.colacion_fin) &&
  (!d.colacion_inicio || !d.colacion_fin || !(d.inicio < d.colacion_inicio && d.colacion_inicio < d.colacion_fin && d.colacion_fin < d.fin))

// Mantenimiento's working hours: when work runs as normal time, and each
// day's lunch break (colación), when the count stops and then picks up by
// itself. Outside working time a capsule needs authorization and counts as
// overtime; a running one stops by itself at the end of the day's jornada.
// Company holidays never are working days. Read-only for those who may not
// edit Mantenimiento.
export default function HorarioLaboral() {
  const { t } = useTranslation('engineering')
  const puedeEditar = usePuedeEditarMantenimiento()
  const [dias, setDias] = useState(null)
  const [guardado, setGuardado] = useState(null)
  const [error, setError] = useState('')
  const [ok, setOk] = useState(false)
  const [guardando, setGuardando] = useState(false)

  useEffect(() => {
    getHorarioLaboral()
      .then(h => { const d = h.map(aForm); setDias(d); setGuardado(JSON.stringify(d)) })
      .catch(() => setError(t('capsulas.cargaFallo')))
  }, [t])

  if (!dias) return error ? <p className="text-red-500 text-sm">{error}</p> : <p className="text-gray-400 text-sm">{t('unidades.loading')}</p>

  const cambiar = (i, cambios) => { setOk(false); setDias(ds => ds.map((d, k) => (k === i ? { ...d, ...cambios } : d))) }
  const invalido = dias.find(d => ((d.inicio || d.fin) && (!d.inicio || !d.fin || d.inicio >= d.fin)) || ((d.inicio || d.fin) && colacionMala(d)))
  const guardar = async () => {
    setGuardando(true)
    setError('')
    try {
      const h = await putHorarioLaboral(dias.map(d => ({ dia_semana: d.dia_semana, inicio: d.inicio || null, fin: d.fin || null,
        colacion_inicio: (d.inicio && d.colacion_inicio) || null, colacion_fin: (d.inicio && d.colacion_fin) || null })))
      const nd = h.map(aForm)
      setDias(nd)
      setGuardado(JSON.stringify(nd))
      setOk(true)
    } catch { setError(t('capsulas.horario.fallo')) }
    setGuardando(false)
  }

  return (
    <div className="max-w-3xl space-y-4">
      <p className="text-sm text-gray-600">{t('capsulas.horario.hint')}</p>
      <div className="rounded-xl border border-gray-200 bg-white divide-y divide-gray-100">
        {dias.map((d, i) => {
          const laboral = !!(d.inicio || d.fin)
          if (!puedeEditar) {
            return (
              <div key={d.dia_semana} className="flex items-center gap-4 px-4 py-2.5 text-sm">
                <span className="w-36 font-medium text-gray-800">{t(`capsulas.horario.dias.${DIAS[d.dia_semana]}`)}</span>
                {laboral ? (
                  <span className="text-gray-700 tabular-nums">
                    {d.inicio} – {d.fin}
                    {d.colacion_inicio && (
                      <span className="ml-3 text-xs text-gray-500">{t('capsulas.horario.colacion')} {d.colacion_inicio} – {d.colacion_fin}</span>
                    )}
                  </span>
                ) : <span className="text-gray-400">{t('capsulas.horario.noLaboral')}</span>}
              </div>
            )
          }
          return (
            <div key={d.dia_semana} className="flex items-center gap-4 px-4 py-2.5">
              <label className="flex items-center gap-2 w-36 text-sm font-medium text-gray-800 cursor-pointer">
                <input type="checkbox" checked={laboral}
                  onChange={e => cambiar(i, e.target.checked
                    ? { inicio: '08:00', fin: '18:00', colacion_inicio: '13:00', colacion_fin: '14:00' }
                    : { inicio: '', fin: '', colacion_inicio: '', colacion_fin: '' })} />
                {t(`capsulas.horario.dias.${DIAS[d.dia_semana]}`)}
              </label>
              {laboral ? (
                <div className="flex items-center gap-2 text-sm">
                  <input type="time" value={d.inicio} onChange={e => cambiar(i, { inicio: e.target.value })}
                    className="border border-gray-200 rounded-lg px-2 py-1" />
                  <span className="text-gray-400">–</span>
                  <input type="time" value={d.fin} onChange={e => cambiar(i, { fin: e.target.value })}
                    className="border border-gray-200 rounded-lg px-2 py-1" />
                  <label className="flex items-center gap-1.5 ml-3 text-xs text-gray-600 cursor-pointer">
                    <input type="checkbox" checked={!!(d.colacion_inicio || d.colacion_fin)}
                      onChange={e => cambiar(i, e.target.checked ? { colacion_inicio: '13:00', colacion_fin: '14:00' } : { colacion_inicio: '', colacion_fin: '' })} />
                    {t('capsulas.horario.colacion')}
                  </label>
                  {(d.colacion_inicio || d.colacion_fin) && (
                    <>
                      <input type="time" value={d.colacion_inicio} onChange={e => cambiar(i, { colacion_inicio: e.target.value })}
                        className={`border rounded-lg px-2 py-1 ${colacionMala(d) ? 'border-red-300' : 'border-gray-200'}`} />
                      <span className="text-gray-400">–</span>
                      <input type="time" value={d.colacion_fin} onChange={e => cambiar(i, { colacion_fin: e.target.value })}
                        className={`border rounded-lg px-2 py-1 ${colacionMala(d) ? 'border-red-300' : 'border-gray-200'}`} />
                    </>
                  )}
                </div>
              ) : <span className="text-sm text-gray-400">{t('capsulas.horario.noLaboral')}</span>}
            </div>
          )
        })}
      </div>
      <p className="text-xs text-gray-500">{t('capsulas.horario.feriados')}</p>
      {puedeEditar && invalido && <p className="text-red-500 text-xs">{t('capsulas.horario.invalido')}</p>}
      {error && <p className="text-red-500 text-xs">{error}</p>}
      {puedeEditar && <div className="flex items-center gap-3">
        <button type="button" onClick={guardar} disabled={guardando || !!invalido || JSON.stringify(dias) === guardado}
          className="bg-gray-900 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-gray-800 disabled:opacity-40">
          {guardando ? t('unidades.saving') : t('unidades.save')}
        </button>
        {ok && <span className="text-sm text-emerald-700">{t('capsulas.horario.guardado')}</span>}
      </div>}
    </div>
  )
}
